import { useCallback, useEffect } from 'react'
import { Cross } from '@leroy-merlin-br/backyard-icons'
import { Text, Icon, Inline } from '@leroy-merlin-br/backyard-react'
import { useClearRefinements, useCurrentRefinements } from 'react-instantsearch'
import { CloseButton } from '@backyard-ui/core'

import { onFilterDataLayer } from 'site/AlgoliaEntryKey/utils/data-layer'
import { useAlgoliaContext } from 'site/AlgoliaEntryKey/context/algolia-context'

import formatPriceToBRL from 'utils/formatPriceToBRL'

import * as S from './styled'
import { Card } from '../Card'

type RefinementsProps = {
  value: string | number
  label: string
  isRefined?: boolean
  attribute: string
  type:
    | 'facet'
    | 'exclude'
    | 'disjunctive'
    | 'hierarchical'
    | 'numeric'
    | 'query'
    | 'tag'
}

type ItemsProps = {
  refinements: RefinementsProps[]
}

const AppliedFilters = () => {
  const { refine: cleanFilters } = useClearRefinements()
  const { items, refine } = useCurrentRefinements()
  const { gaTrack, pageType, query } = useAlgoliaContext()
  const renderAppliedFiltersLabel = (refinement: RefinementsProps) => {
    const { attribute, label } = refinement

    const attributesLabel = {
      'relevanceFields.novelty': 'Novidades',
      'relevanceFields.exclusiveBrand': 'Marca Própria',
      'relevanceFields.exclusiveMarkeplace': 'Exclusivo Leroy Merlin',
      'relevanceFields.hasStock': 'Em estoque',
      'offer.enabled': 'Somente Ofertas'
    }

    const attributeLabel =
      attributesLabel[attribute as keyof typeof attributesLabel]

    if (attributeLabel) {
      return <S.Label>{attributeLabel}</S.Label>
    }

    return <S.Label>{label}</S.Label>
  }

  const sendEvent = useCallback(
    (value: RefinementsProps) => {
      const url = new URL(window.location.href)

      onFilterDataLayer({
        gaTrack,
        term: query,
        pageType,
        filter: 'Normal',
        filterType: { index: 0, value: value.attribute },
        filterElement: { index: 0, value: value.value },
        isParamAlreadySet: url.searchParams.has(value.attribute)
      })
    },
    [gaTrack, pageType, query]
  )

  const renderPriceLabel = (item: ItemsProps) => {
    const minPrice = formatPriceToBRL(item.refinements[0]?.value).replace(
      ',00',
      ''
    )
    const maxPrice = formatPriceToBRL(item.refinements[1]?.value).replace(
      ',00',
      ''
    )

    if (item.refinements.length > 1) {
      return <S.Label>{`${minPrice} a ${maxPrice}`}</S.Label>
    }
    return <S.Label>{`Até ${minPrice}`}</S.Label>
  }

  const ClosePriceTag = (event: Event, item: ItemsProps) => {
    event.preventDefault()
    event.stopPropagation()

    if (item.refinements.length > 1) {
      item.refinements.forEach((refinement: RefinementsProps) => {
        sendEvent(refinement)
        refine(refinement)
      })
    } else {
      sendEvent(item.refinements[0])
      refine(item.refinements[0])
    }
  }

  useEffect(() => {
    if (query) {
      cleanFilters()
    }
  }, [cleanFilters, query])

  if (!items.length) {
    return null
  }

  return (
    <S.Wrapper>
      <Card target="card">
        <Inline alignY="center" alignX="space-between">
          <Text as="strong" size="giga" isBold noMargin>
            Filtros selecionados
          </Text>

          <CloseButton
            size="xs"
            title="Limpar filtros"
            onClick={() => {
              sendEvent({
                attribute: 'clean all',
                label: 'Limpar todos os filtros',
                value: 'clean all',
                type: 'tag',
                isRefined: false
              })
              cleanFilters()
            }}
          />
        </Inline>

        <Inline alignY="center" alignX="flex-start" space="byte" wrap="wrap">
          {items.map((item) => (
            <Inline
              alignY="center"
              alignX="flex-start"
              space="byte"
              wrap="wrap"
            >
              {item.attribute === 'price.promotional' ? (
                <S.Tag key={item.attribute}>
                  {renderPriceLabel(item)}

                  <S.ButtonClose
                    type="button"
                    onClick={(event: Event) => ClosePriceTag(event, item)}
                  >
                    <Icon as={Cross} size="byte" />
                  </S.ButtonClose>
                </S.Tag>
              ) : (
                item.refinements.map((refinement) => (
                  <S.Tag key={refinement.value}>
                    {renderAppliedFiltersLabel(refinement)}
                    <S.ButtonClose
                      type="button"
                      onClick={(event: Event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        sendEvent(refinement)
                        refine(refinement)
                      }}
                    >
                      <Icon as={Cross} size="byte" />
                    </S.ButtonClose>
                  </S.Tag>
                ))
              )}
            </Inline>
          ))}
        </Inline>
      </Card>
    </S.Wrapper>
  )
}

export default AppliedFilters
